import { useState, useEffect } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import { FaBook, FaEdit, FaTrash, FaPlus, FaKey, FaUserLock } from "react-icons/fa";
import appConstants from "../constants/appConstants";
import Layout from "../components/Layout";
import { Link } from "react-router-dom";

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const initialFormData = {
    id:"",
    staff_code: "",
    name: "",
    address: "",
    gender: "Male",
    dob: "",
    mobile: "",
    email: "",
    is_teacher: false,
    pic: "",
    password: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchStaff();
  }, []);

  const getAuthToken = () => localStorage.getItem("authToken"); // Retrieve token from localStorage

  const fetchStaff = async () => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    try {
      const response = await fetch(`${appConstants.API_ROOT_URL}get-staff`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setStaff(data.data);
      } else {
        console.error("Error fetching staff:", data.message);
      }
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  const handleCloseModal = () => {
    setEditingStaff(null);
    setShowModal(false);
    setFormData(initialFormData);
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token found");
      return;
    }

    try {
      const response = await fetch(`${appConstants.API_ROOT_URL}delete-staff/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchStaff();
      } else {
        const data = await response.json();
        console.error("Error deleting staff:", data.message);
      }
    } catch (error) {
      console.error("Failed to delete staff:", error);
    }
  };

  const handleSave = async () => {
    const token = getAuthToken();
    if (!token) {
      alert("Authentication failed. Please log in again.");
      return;
    }
  
    const url = editingStaff
      ? `${appConstants.API_ROOT_URL}update-staff`
      : `${appConstants.API_ROOT_URL}create-staff`;
  
    try {
      console.log(formData);
  
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
  
      const data = await response.json(); // Parse response JSON
  
      if (response.ok) {
        setShowModal(false);
        fetchStaff();
        //alert("Staff member saved successfully.");
      } else {
        console.error("Error saving staff:", data.message || "Unknown error");
        alert(data.message || "Failed to save staff. Please try again.");
      }
    } catch (error) {
      console.error("Failed to save staff:", error);
      alert("An unexpected error occurred. Please try again.");
    }
  };
  

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setFormData(staff);
    setShowModal(true);
  };

  return (
    <Layout>
      <h3>Staff Management</h3>
      <Button variant="primary" onClick={() => setShowModal(true)}>
        <FaPlus /> Add Staff
      </Button>
      <Table striped bordered hover className="mt-3">
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(staff) &&
            staff.map((s, index) => (
              <tr key={s.id}>
                <td>{index + 1}</td>
                <td>{s.staff_code} - {s.name}</td>
                <td>{s.email}</td>
                <td>
                  <Button variant="warning" size="sm" onClick={() => handleEdit(s)}>
                    <FaEdit />
                  </Button>
                  <Button style={{display:"none"}} variant="danger" size="sm" onClick={() => handleDelete(s.id)}>
                    <FaTrash />
                  </Button>
                  <Link to={`/staff-subjects/${s.id}`}>
                    <Button variant="info" size="sm" className="ms-2">
                      <FaBook />
                    </Button>
                  </Link>
                  <Link to={`/staff-permission/${s.id}`}>
                    <Button variant="warning" size="sm" className="ms-2">
                    <FaUserLock />
                    </Button>
                  </Link>

                </td>
              </tr>
            ))}
        </tbody>
      </Table>
      <Modal show={showModal} onHide={() => handleCloseModal()}>
        <Modal.Header closeButton>
          <Modal.Title>{editingStaff ? "Edit Staff" : "Add Staff"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Staff Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.staff_code}
                onChange={(e) => setFormData({ ...formData, staff_code: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Gender</Form.Label>
              <Form.Control
                as="select"
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </Form.Control>
            </Form.Group>
            <Form.Group>
              <Form.Label>DOB</Form.Label>
              <Form.Control
                type="date"
                value={formData.dob}
                onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Mobile</Form.Label>
              <Form.Control
                type="text"
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Form.Group>

            {!editingStaff && (
              <>
            <Form.Group>
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </Form.Group>
            </>
            )}

          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => handleCloseModal()}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default StaffManagement;
